import { Pencil, Plus, Store, Users, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode, type Ref } from "react";
import { currencies, roles } from "../../shared/types";
import { patch, post } from "../api";
import { ErrorState, Field, InlineAlert, LoadingState, PageHeader, StatusBadge } from "../components/common";
import { useAppContext } from "../context";
import { roleLabels } from "../format";
import { useApiQuery } from "../hooks";
import { ProfitMasterSettings } from "../components/ProfitMasterSettings";

type SettingsData = {
  platforms: Array<{ id: string; code: string; name: string }>;
  stores: Array<{ id: string; platform_id: string; code: string; name: string; site: string; settlement_currency: string; platform_name: string; active: boolean; version: number }>;
  users: Array<{ id: string; name: string; email: string; role: string; active: boolean; version: number }>;
};

export function SettingsPage() {
  const { refreshBootstrap, setUnsavedChanges } = useAppContext();
  const { data, error, loading, reload } = useApiQuery<SettingsData>("/api/settings");
  const [kind, setKind] = useState<"store" | "user">("store");
  const [values, setValues] = useState<Record<string, string>>({});
  const [editingStore, setEditingStore] = useState<SettingsData["stores"][number] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const storeCodeRef = useRef<HTMLInputElement | null>(null);
  const editTriggerRef = useRef<HTMLButtonElement | null>(null);
  const dirty = editingStore !== null || Object.values(values).some((value) => value !== "");
  const set = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setNotice(null);
  };

  useEffect(() => setUnsavedChanges(dirty), [dirty, setUnsavedChanges]);
  useEffect(() => () => setUnsavedChanges(false), [setUnsavedChanges]);
  useEffect(() => {
    if (!editingStore) return;
    document.getElementById("master-data-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    const frame = requestAnimationFrame(() => storeCodeRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [editingStore]);

  if (loading && !data) return <LoadingState />;
  if (error && !data) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      if (kind === "store") {
        const storePayload = {
          platformId: values.platformId,
          code: values.code,
          name: values.name,
          site: values.site,
          settlementCurrency: values.currency
        };
        if (editingStore) {
          await patch(`/api/settings/stores/${editingStore.id}`, { ...storePayload, version: editingStore.version, reason: "修正店铺基础资料" });
        } else {
          await post("/api/settings/stores", storePayload);
        }
      } else {
        await post("/api/settings/users", {
          name: values.name,
          email: values.email,
          role: values.role,
          password: values.password
        });
      }
      setValues({});
      setEditingStore(null);
      setUnsavedChanges(false);
      setNotice("已保存基础资料并写入操作记录。");
      if (kind === "store") {
        reload();
        await refreshBootstrap();
        return;
      }
      reload();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  function beginEditStore(store: SettingsData["stores"][number], trigger: HTMLButtonElement) {
    editTriggerRef.current = trigger;
    setKind("store");
    setEditingStore(store);
    setValues({
      platformId: store.platform_id,
      code: store.code,
      name: store.name,
      site: store.site,
      currency: store.settlement_currency
    });
    setNotice(null);
  }

  function cancelStoreEdit() {
    const trigger = editTriggerRef.current;
    setEditingStore(null);
    setValues({});
    setNotice(null);
    requestAnimationFrame(() => trigger?.focus());
  }

  async function toggleStore(store: SettingsData["stores"][number]) {
    if (store.active && !window.confirm(`确定停用店铺“${store.name}”吗？历史对账记录仍会保留。`)) return;
    setBusy(true);
    setNotice(null);
    try {
      await post(`/api/settings/stores/${store.id}/toggle`, { version: store.version, active: !store.active, reason: `${store.active ? "停用" : "启用"}对账店铺` });
      setNotice(`已${store.active ? "停用" : "启用"}店铺，历史记录保持不变。`);
      reload();
      await refreshBootstrap();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  async function toggleUser(user: SettingsData["users"][number]) {
    setBusy(true);
    try {
      await post(`/api/settings/users/${user.id}/toggle`, { version: user.version, active: !user.active, reason: `${user.active ? "停用" : "启用"}对账账号` });
      setNotice(`账号已${user.active ? "停用" : "启用"}。`);
      reload();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  return <div className="page settings-page compact-settings">
    <PageHeader eyebrow="基础设置" title="店铺、账号、SKU成本与汇率" description="先把真实店铺、产品生效日成本和汇率维护清楚，订单导入时会冻结所用版本并保留来源。" />
    {notice ? <InlineAlert tone={notice.startsWith("已") ? "success" : "danger"}>{notice}</InlineAlert> : null}
    {!data.stores.length ? <InlineAlert>还没有可用店铺。请先在下方录入真实的平台、店铺编码、站点和对账币种，保存后即可开始对账。</InlineAlert> : null}
    <section className={`ledger-section master-data-form ${editingStore ? "editing" : ""}`} id="master-data-form">
      <div className="section-heading"><div><span className="eyebrow">管理员</span><h2>{editingStore ? `修改店铺 · ${editingStore.name}` : "新增基础资料"}</h2></div>{editingStore ? <Pencil size={20} /> : <Plus size={20} />}</div>
      <form onSubmit={submit}>
        <Field label="资料类型"><select value={kind} disabled={Boolean(editingStore)} onChange={(event) => { setKind(event.target.value as typeof kind); setValues({}); setEditingStore(null); setNotice(null); }}><option value="store">平台店铺</option><option value="user">内部账号</option></select></Field>
        {kind === "store" ? <>
          <Field label="平台"><select required value={values.platformId ?? ""} onChange={(event) => set("platformId", event.target.value)}><option value="">请选择</option>{data.platforms.map((platform) => <option key={platform.id} value={platform.id}>{platform.name}</option>)}</select></Field>
          <TextField label="店铺编码" value={values.code} onChange={(value) => set("code", value)} inputRef={storeCodeRef} />
          <TextField label="店铺名称" value={values.name} onChange={(value) => set("name", value)} />
          <TextField label="站点" value={values.site} onChange={(value) => set("site", value)} />
          <Field label="对账币种" hint={editingStore ? "已有账务记录时不可更改" : undefined}><select required value={values.currency ?? ""} onChange={(event) => set("currency", event.target.value)}><option value="">请选择</option>{currencies.map((currency) => <option key={currency}>{currency}</option>)}</select></Field>
        </> : <>
          <TextField label="姓名" value={values.name} onChange={(value) => set("name", value)} />
          <TextField label="邮箱" type="email" value={values.email} onChange={(value) => set("email", value)} />
          <Field label="角色"><select required value={values.role ?? ""} onChange={(event) => set("role", event.target.value)}><option value="">请选择</option>{roles.map((role) => <option key={role} value={role}>{roleLabels[role]}</option>)}</select></Field>
          <TextField label="初始密码" type="password" value={values.password} onChange={(value) => set("password", value)} />
        </>}
        <div className="master-data-actions">
          {editingStore ? <button type="button" className="button secondary" onClick={cancelStoreEdit}><X size={16} /> 取消修改</button> : null}
          <button className="button primary" disabled={busy}>{editingStore ? <Pencil size={16} /> : <Plus size={16} />} {busy ? "正在保存…" : editingStore ? "保存修改" : "保存"}</button>
        </div>
      </form>
    </section>
    <div className="settings-grid minimal-settings-grid">
      <SettingSection icon={<Store size={19} />} title={`平台店铺 · ${data.stores.length}`}>{data.stores.length ? data.stores.map((store) => <div className="setting-row store" key={store.id}>
        <div><strong>{store.name}</strong><small>{store.platform_name} · {store.site}</small></div>
        <code>{store.code}</code><span className="setting-currency">{store.settlement_currency}</span><StatusBadge status={store.active ? "active" : "inactive"} />
        <div className="setting-actions"><button className="text-button" disabled={busy} aria-label={`修改店铺 ${store.name}`} onClick={(event) => beginEditStore(store, event.currentTarget)}>修改</button><button className="text-button" disabled={busy} onClick={() => void toggleStore(store)}>{store.active ? "停用" : "启用"}</button></div>
      </div>) : <div className="empty-row">暂无店铺，请先在上方新增真实店铺。</div>}</SettingSection>
      <SettingSection icon={<Users size={19} />} title={`内部账号 · ${data.users.length}`}>{data.users.map((user) => <div className="setting-row user" key={user.id}><span className="user-avatar small">{user.name.slice(0, 1)}</span><div><strong>{user.name}</strong><small>{user.email}</small></div><span className="setting-user-role">{roleLabels[user.role] ?? user.role}</span><StatusBadge status={user.active ? "active" : "inactive"} /><button className="text-button" disabled={busy} onClick={() => void toggleUser(user)}>{user.active ? "停用" : "启用"}</button></div>)}</SettingSection>
    </div>
    <ProfitMasterSettings />
  </div>;
}

function TextField({ label, value = "", onChange, type = "text", inputRef }: { label: string; value?: string; onChange(value: string): void; type?: string; inputRef?: Ref<HTMLInputElement> }) {
  return <Field label={label}><input ref={inputRef} required type={type} value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
}

function SettingSection({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return <section className="ledger-section setting-section"><div className="section-heading compact"><h2>{title}</h2>{icon}</div><div className="setting-list">{children}</div></section>;
}
